import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import logger from '@/util/logger';
import { createId, HomeEvalJobDao } from '@rock/db-admin';
import type { HomeEvalJobId, HomeId } from '@rock/types';
import { HomeEvalJobSchema } from '@rock/types';
import { Play } from 'lucide-react';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

const log = logger('CreateHomeEvalJobButton');
const homeEvalJobDao = new HomeEvalJobDao();

export const CreateHomeEvalJobButton: FC = () => {
    const [open, setOpen] = useState(false);
    const [homeId, setHomeId] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const closeDialog = useCallback(() => {
        setHomeId('');
        setOpen(false);
    }, []);

    const handleSubmit = useCallback(
        async (e: React.FormEvent) => {
            e.preventDefault();
            if (!homeId.trim()) {
                toast.error('Home ID is required');
                return;
            }

            setSubmitting(true);
            try {
                const jobId = createId<HomeEvalJobId>();
                const scheduledAt = new Date().toISOString();
                const job = HomeEvalJobSchema.decode({
                    object: 'home-eval-job',
                    schema: 1,
                    id: jobId,
                    homeId: homeId.trim() as HomeId,
                    jobStatus: 'pending',
                    attempts: 0,
                    scheduledAt,
                });

                await homeEvalJobDao.set(jobId, job);

                toast.success('Home eval job created', {
                    description: `Job ${jobId} has been queued for Home ${homeId}`,
                });
                log.info('Created home eval job manually', { homeId, jobId });
                closeDialog();
            } catch (err) {
                log.error(`Failed to create home eval job (homeId: ${homeId})`, { err });
                toast.error('Unable to create job', {
                    description: err instanceof Error ? err.message : 'Unknown error',
                });
            } finally {
                setSubmitting(false);
            }
        },
        [homeId, closeDialog],
    );

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !submitting && setOpen(isOpen)}>
            <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="cursor-pointer">
                    <Play className="h-4 w-4 mr-2" />
                    Run Job
                </Button>
            </DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Create Home Eval Job</DialogTitle>
                    <DialogDescription>
                        Manually trigger a home evaluation job for a specific home. This will bypass the normal trigger
                        system and create a job directly.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="homeId">Home ID</Label>
                            <Input
                                id="homeId"
                                placeholder="Paste home ID here"
                                value={homeId}
                                onChange={(e) => setHomeId(e.target.value)}
                                disabled={submitting}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="cursor-pointer"
                            disabled={submitting}
                            onClick={closeDialog}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" className="cursor-pointer" disabled={submitting}>
                            {submitting ? 'Creating...' : 'Create Job'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};
